import { useState, useEffect } from 'react';
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogContent,
  DialogActions,
  Dropdown,
  Field,
  Option,
  Textarea,
} from '@fluentui/react-components';
import { type UseMutationResult } from '@tanstack/react-query';
import { type TaxReturn } from '../../../types';

export interface EditEfileStatusDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialEfileStatus: string;
  initialRejectionReason: string;
  updateMutation: UseMutationResult<unknown, Error, Partial<TaxReturn>>;
}

export function EditEfileStatusDialog({
  open,
  onOpenChange,
  initialEfileStatus,
  initialRejectionReason,
  updateMutation,
}: EditEfileStatusDialogProps) {
  const [selectedEfileStatus, setSelectedEfileStatus] = useState(initialEfileStatus);
  const [efileRejectionReason, setEfileRejectionReason] = useState(initialRejectionReason);

  useEffect(() => {
    if (open) {
      setSelectedEfileStatus(initialEfileStatus);
      setEfileRejectionReason(initialRejectionReason);
    }
  }, [open, initialEfileStatus, initialRejectionReason]);

  const handleSave = () => {
    const updates: Partial<TaxReturn> = {
      efileStatus: selectedEfileStatus as 'pending' | 'submitted' | 'accepted' | 'rejected',
    };

    if (selectedEfileStatus === 'submitted') {
      updates.efileSubmittedDate = new Date().toISOString();
    } else if (selectedEfileStatus === 'accepted') {
      updates.efileAcceptedDate = new Date().toISOString();
    } else if (selectedEfileStatus === 'rejected') {
      updates.efileRejectionReason = efileRejectionReason;
    }

    updateMutation.mutate(updates, {
      onSuccess: () => {
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(_, data) => onOpenChange(data.open)}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle>Update E-File Status</DialogTitle>
          <DialogContent>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <Field label="E-File Status">
                <Dropdown
                  value={
                    selectedEfileStatus === 'submitted' ? 'Submitted' :
                    selectedEfileStatus === 'accepted' ? 'Accepted' :
                    selectedEfileStatus === 'rejected' ? 'Rejected' : 'Pending'
                  }
                  selectedOptions={selectedEfileStatus ? [selectedEfileStatus] : []}
                  onOptionSelect={(_, data) => setSelectedEfileStatus(data.optionValue as string)}
                  style={{ width: '100%' }}
                >
                  <Option value="pending">Pending</Option>
                  <Option value="submitted">Submitted</Option>
                  <Option value="accepted">Accepted</Option>
                  <Option value="rejected">Rejected</Option>
                </Dropdown>
              </Field>
              {selectedEfileStatus === 'rejected' && (
                <Field label="Rejection Reason">
                  <Textarea
                    value={efileRejectionReason}
                    onChange={(_, data) => setEfileRejectionReason(data.value)}
                    placeholder="Enter the reason for rejection..."
                    rows={3}
                  />
                </Field>
              )}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              appearance="primary"
              disabled={!selectedEfileStatus || updateMutation.isPending}
              onClick={handleSave}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
